import styled, { createGlobalStyle, css } from 'styled-components'
import { colors, fontScope, visuallyHidden } from '../../components/SalesReport/tokens'

const MOBILE = '@media (max-width: 767px)'

export const Page = styled.div`
  ${fontScope}
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 64px 0;
  box-sizing: border-box;

  ${MOBILE} {
    padding: 27px 16px 0;
  }
`

export const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;

  ${MOBILE} {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 12px;
  }
`

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
  min-width: 260px;

  ${MOBILE} {
    flex: none;
    width: 100%;
    min-width: 0;
    gap: 4px;
  }
`

export const Overline = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 19.5px;
  letter-spacing: 0.52px;
  text-transform: uppercase;
  color: ${colors.ink500};

  ${MOBILE} {
    font-size: 12px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.5px;
  }
`

export const Title = styled.h1`
  margin: 0;
  padding-top: 4px;
  font-size: 30px;
  font-weight: 800;
  line-height: 33px;
  letter-spacing: -0.6px;
  color: ${colors.ink950};

  ${MOBILE} {
    padding-top: 0;
    font-size: 24px;
    line-height: 28px;
    letter-spacing: 0;
  }
`

export const Description = styled.p`
  margin: 0;
  padding-top: 6px;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: ${colors.ink500};

  ${MOBILE} {
    padding-top: 0;
    font-size: 13px;
    line-height: 18px;
  }
`

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-top: 4px;

  ${MOBILE} {
    flex-wrap: nowrap;
    width: 100%;
    padding-top: 0;
  }
`

export const ActionButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9.8px 16.8px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
  white-space: nowrap;
  cursor: pointer;
  ${({ $primary }) =>
    $primary
      ? css`
          border: 0.8px solid ${colors.primary};
          background: ${colors.primary};
          color: ${colors.white};
        `
      : css`
          border: 0.8px solid ${colors.border};
          background: ${colors.white};
          color: ${colors.ink800};
        `}

  ${MOBILE} {
    gap: 6px;
    padding: 8px 12px;
    border-width: 1px;
    font-size: 12px;
    line-height: normal;
    color: ${({ $primary }) => ($primary ? colors.white : colors.ink700)};
    ${({ $primary }) =>
      $primary &&
      css`
        flex: 1 0 0;
        min-width: 0;
      `}
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`

export const DesktopOnly = styled.span`
  display: inline-flex;

  ${MOBILE} {
    display: none;
  }
`

export const MobileOnly = styled.span`
  display: none;

  ${MOBILE} {
    display: inline-flex;
  }
`

export const Hidden = styled.span`
  ${visuallyHidden}
`

export const ExportStatus = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.ink600};

  &:empty {
    display: none;
  }
`

export const ExportError = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primaryStrong};
`

export const PrintStyle = createGlobalStyle`
  @page {
    size: A4 landscape;
    margin: 10mm;
  }

  @media print {
    html,
    body {
      overflow: visible !important;
    }

    [data-sales-report-print],
    [data-sales-report-print] * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body * {
      visibility: hidden;
    }

    [data-sales-report-print],
    [data-sales-report-print] * {
      visibility: visible;
    }

    [data-sales-report-print] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      max-width: none;
      padding: 0;
    }

    [data-print-hide] {
      display: none !important;
    }
  }
`
